export function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function parseOptionalStringArrayField(
  source: Record<string, unknown>,
  key: string,
  label = key,
): Array<string> {
  const value = parseOptionalArrayField(source, key, label);
  const invalidIndex = value.findIndex((item) => typeof item !== 'string');
  if (invalidIndex >= 0) {
    throw new Error(`${label} must contain only strings`);
  }

  return value as Array<string>;
}

export function parseEnabledRegionsField(source: Record<string, unknown>, label: string): Array<string> {
  if (source.enabledRegions !== undefined) {
    return parseOptionalStringArrayField(source, 'enabledRegions', label);
  }
  if (source.eligibleRegions !== undefined) {
    return parseOptionalStringArrayField(source, 'eligibleRegions', `${label} (legacy eligibleRegions)`);
  }
  return [];
}

export function parseEligibleRegionsField(source: Record<string, unknown>, label: string): Array<string> {
  if (source.eligibleRegions !== undefined) {
    return parseOptionalStringArrayField(source, 'eligibleRegions', label);
  }
  if (source.enabledRegions !== undefined) {
    return parseOptionalStringArrayField(source, 'enabledRegions', `${label} (legacy enabledRegions)`);
  }
  return [];
}

export function parseNonEmptyStrings(values: Array<string>): Array<string> {
  return values.map((value) => value.trim()).filter(Boolean);
}

export function hasNonEmptyString(value: string): boolean {
  return value.trim().length > 0;
}

export function nonEmptyOrDefault<T>(items: T[], fallback: T[]): T[] {
  return items.length > 0 ? items : fallback;
}

export function parseOptionalObjectField(source: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = source[key];
  if (value === undefined) {
    return {};
  }

  return parseObject(value, key);
}

export function parseObject(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }

  return value as Record<string, unknown>;
}

export function isKnownValue<T extends string>(value: string, knownValues: readonly T[]): value is T {
  return knownValues.includes(value as T);
}

export function parseKnownValue<T extends string>(value: unknown, knownValues: readonly T[], label: string): T {
  if (typeof value === 'string' && isKnownValue(value, knownValues)) {
    return value;
  }

  throw new Error(`${label} must be ${knownValues.join(' or ')}`);
}

export function parseOptionalArrayField(source: Record<string, unknown>, key: string, label = key): Array<unknown> {
  const value = source[key];
  if (value === undefined) {
    return [];
  }
  if (!Array.isArray(value)) {
    throw new Error(`${label} must be an array`);
  }

  return value;
}

export function parseRequiredArrayField(source: Record<string, unknown>, key: string): Array<unknown> {
  const value = source[key];
  if (value === undefined) {
    throw new Error(`${key} is required`);
  }
  if (!Array.isArray(value)) {
    throw new Error(`${key} must be an array`);
  }

  return value;
}

export function parseJsonObject(raw: string): Record<string, unknown> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Invalid JSON';
    throw new Error(message, { cause: error });
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Payload must be a JSON object');
  }

  return parsed as Record<string, unknown>;
}
